import { apiClient } from './apiClient';
import type { RoleType, Scope, UserScopedRole } from '@/types/role';

/**
 * `/auth/me` — single request that hydrates identity, JWT claims, scoped
 * roles, and a convenience `is_system_admin` flag derived server-side.
 *
 * Prefer this over separately calling /user-scoped-roles for the current
 * user: one round trip, authoritative source.
 */

/** The caller's GizmoData Cloud email preference (Profile → Email). */
export interface EmailPreference {
  subscribed: boolean;
  unsubscribedAt: string | null;
  /** This deployment sends GizmoData Cloud emails at all (customer-hosted installs don't). */
  available: boolean;
}

export interface MeResponse {
  id: string;
  name: string;
  label: string;
  createdByUserId: string;
  createdAt: string;
  updatedByUserId: string;
  updatedAt: string;
  tokenClaims?: Record<string, unknown> | null;
  scopedRoles: UserScopedRole[];
  isSystemAdmin: boolean;
  /** Deployment-level: whether a NEW org may select self-managed (BYO) infra.
   *  Folds in the ALLOW_SELF_MANAGED_INFRASTRUCTURE kill switch + billing mode +
   *  the system-admin carve-out, computed server-side. */
  selfManagedInfrastructureAllowed: boolean;
  /** Whether THIS user may create a new organization. False for non-system-admins
   *  on a customer_hosted control plane (the API 403s POST /organizations/ there —
   *  the customer's org already exists; everyone else joins by invitation). */
  organizationCreationAllowed: boolean;
  /** Whether a user's first organization needs a signup-invite code right now (public cloud). */
  signupInviteRequired: boolean;
  /** Deployment-level: whether billing/plan features are active (Stripe configured).
   *  False on self-hosted / customer-hosted deploys — the portal hides billing UI. */
  billingEnabled: boolean;
  /** Global compute cost markup applied to displayed prices (1.0 self-hosted, e.g. 3.0
   *  Public Cloud). Multiply the base estimate × this × the license multiplier. */
  computeCostMultiplier: number;
  /** $/GB-month for managed storage (0 = not billed on this deployment). */
  storagePricePerGbMonth: number;
  /** Deployment banner (env differentiation): null/undefined = no banner. */
  portalBannerText?: string | null;
  /** Deployment Grafana base URL; null = no monitoring stack (hide Grafana links). */
  grafanaBaseUrl?: string | null;
  portalBannerColor?: string;
  /** Guided tour: the portal offers the tour by itself only while both are null. */
  tourCompletedAt?: string | null;
  tourDismissedAt?: string | null;
  /** Whether the auto-terminate job runs on this deployment (off ⇒ the setting does nothing). */
  autoTerminateEnabled: boolean;
  /** Idle stop: the job that stops idle clusters runs here (off ⇒ hide every idle-stop control). */
  idleStopEnabled: boolean;
  /** The create wizard's "Stop when idle" switch starts on (public cloud) or off (customer-hosted). */
  idleStopOnByDefault: boolean;
  /** The minutes field's initial value (15). */
  idleStopDefaultMinutes: number;
  /** The range a cluster's idle window may take (5..1440). */
  idleStopMinMinutes: number;
  idleStopMaxMinutes: number;
  /** The longest window an organization that REQUIRES idle stop may choose (60). */
  idleStopRequiredMaxMinutes: number;
}

export type TourAction = 'completed' | 'dismissed' | 'reset';

// The API returns snake_case; normalize to the portal's camelCase shape here
// so callers don't have to reason about casing.
interface ApiMeResponse {
  id: string;
  name: string;
  label: string;
  created_by_user_id: string;
  created_at: string;
  updated_by_user_id: string;
  updated_at: string;
  token_claims?: Record<string, unknown> | null;
  scoped_roles: Array<{
    id: string;
    user_id: string;
    scope: UserScopedRole['scope'];
    scope_id: string;
    role: UserScopedRole['role'];
    created_at: string;
    updated_at: string;
  }>;
  is_system_admin: boolean;
  self_managed_infrastructure_allowed?: boolean;
  organization_creation_allowed?: boolean;
  signup_invite_required?: boolean;
  billing_enabled?: boolean;
  compute_cost_multiplier?: number;
  storage_price_per_gb_month?: number;
  portal_banner_text?: string | null;
  grafana_base_url?: string | null;
  portal_banner_color?: string;
  tour_completed_at?: string | null;
  tour_dismissed_at?: string | null;
  auto_terminate_enabled?: boolean;
  idle_stop_enabled?: boolean;
  idle_stop_on_by_default?: boolean;
  idle_stop_default_minutes?: number;
  idle_stop_min_minutes?: number;
  idle_stop_max_minutes?: number;
  idle_stop_required_max_minutes?: number;
}

const normalize = (api: ApiMeResponse): MeResponse => ({
  id: api.id,
  name: api.name,
  label: api.label,
  createdByUserId: api.created_by_user_id,
  createdAt: api.created_at,
  updatedByUserId: api.updated_by_user_id,
  updatedAt: api.updated_at,
  tokenClaims: api.token_claims,
  scopedRoles: api.scoped_roles.map((r) => ({
    id: r.id,
    userId: r.user_id,
    scope: r.scope,
    scopeId: r.scope_id,
    role: r.role,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  })),
  isSystemAdmin: api.is_system_admin,
  // Default true for back-compat with an older API that doesn't return it.
  selfManagedInfrastructureAllowed: api.self_managed_infrastructure_allowed ?? true,
  // Default true for back-compat: an older API has no such gate, so don't hide the form.
  organizationCreationAllowed: api.organization_creation_allowed ?? true,
  // Default false: an older API that doesn't report it answers a missing code with a 403.
  signupInviteRequired: api.signup_invite_required ?? false,
  // Default false: if the API doesn't report it, assume no billing (safe — hides SaaS UI).
  billingEnabled: api.billing_enabled ?? false,
  // Default 1.0: no markup if the API doesn't report it (safe — shows raw estimate).
  computeCostMultiplier: api.compute_cost_multiplier ?? 1.0,
  storagePricePerGbMonth: api.storage_price_per_gb_month ?? 0,
  portalBannerText: api.portal_banner_text ?? null,
  grafanaBaseUrl: api.grafana_base_url ?? null,
  portalBannerColor: api.portal_banner_color ?? 'amber',
  // Kept undefined when an older API doesn't report them, so the portal never offers the tour there.
  tourCompletedAt: api.tour_completed_at,
  tourDismissedAt: api.tour_dismissed_at,
  autoTerminateEnabled: api.auto_terminate_enabled ?? false,
  // Default off: an older API has no idle-stop job, so the controls stay hidden there.
  idleStopEnabled: api.idle_stop_enabled ?? false,
  idleStopOnByDefault: api.idle_stop_on_by_default ?? false,
  idleStopDefaultMinutes: api.idle_stop_default_minutes ?? 15,
  idleStopMinMinutes: api.idle_stop_min_minutes ?? 5,
  idleStopMaxMinutes: api.idle_stop_max_minutes ?? 1440,
  idleStopRequiredMaxMinutes: api.idle_stop_required_max_minutes ?? 60,
});

/**
 * `/auth/effective-role` — the caller's EFFECTIVE role at a single scope, as
 * resolved by the API's authoritative hierarchical permission resolver. The
 * server walks the scope chain (SYSTEM → org → project/env → PE) and applies
 * the system-admin override, so the portal never re-derives roles. `role` is
 * null when the caller has no access at that scope.
 */
export interface EffectiveRole {
  scope: Scope;
  scopeId: string;
  role: RoleType | null;
  isSystemAdmin: boolean;
}

// Pass exactly the scope you want resolved. If several are supplied the API
// resolves the most specific (PE → environment → project → organization).
export interface EffectiveRoleScope {
  organizationId?: string;
  projectId?: string;
  environmentId?: string;
  projectEnvironmentId?: string;
  /** Most-specific scopes: resolve leaf grants (e.g. a direct admin role on
   *  one cluster or database file) the parent-PE resolution would miss. */
  databaseClusterId?: string;
  databaseId?: string;
  secretId?: string;
}

interface ApiEffectiveRoleResponse {
  scope: Scope;
  scope_id: string;
  role: RoleType | null;
  is_system_admin: boolean;
}

const buildEffectiveRolePath = (scope: EffectiveRoleScope): string | null => {
  const qs = new URLSearchParams();
  // Mirror the server's most-specific-wins precedence so the query is unambiguous.
  if (scope.secretId) qs.set('secret_id', scope.secretId);
  else if (scope.databaseId) qs.set('database_id', scope.databaseId);
  else if (scope.databaseClusterId) qs.set('database_cluster_id', scope.databaseClusterId);
  else if (scope.projectEnvironmentId) qs.set('project_environment_id', scope.projectEnvironmentId);
  else if (scope.environmentId) qs.set('environment_id', scope.environmentId);
  else if (scope.projectId) qs.set('project_id', scope.projectId);
  else if (scope.organizationId) qs.set('organization_id', scope.organizationId);
  else return null;
  return `/auth/effective-role?${qs.toString()}`;
};

export const authService = {
  /**
   * Fetch the authenticated user's /auth/me response. Returns null if the
   * mock fallback has nothing to serve (e.g. demo mode without a seeded user).
   */
  async me(): Promise<MeResponse | null> {
    const response = await apiClient.get<ApiMeResponse>('/auth/me', undefined);
    if (!response.data) return null;
    return normalize(response.data);
  },

  /** Record that the user completed or dismissed the guided tour, or reset it (PUT /auth/me/tour). */
  async setTour(action: TourAction): Promise<void> {
    await apiClient.put('/auth/me/tour', { action });
  },

  /** Whether the caller gets GizmoData Cloud's emails (GET /auth/me/email). */
  async emailPreference(): Promise<EmailPreference> {
    const response = await apiClient.get<{ subscribed: boolean; unsubscribed_at: string | null; available?: boolean }>('/auth/me/email', undefined);
    return { subscribed: response.data?.subscribed ?? true, unsubscribedAt: response.data?.unsubscribed_at ?? null,
             available: response.data?.available ?? false };
  },

  /** Subscribe or unsubscribe the caller (PUT /auth/me/email). */
  async setEmailPreference(subscribed: boolean): Promise<EmailPreference> {
    const response = await apiClient.put<{ subscribed: boolean; unsubscribed_at: string | null; available?: boolean }>('/auth/me/email', { subscribed });
    return { subscribed: response.data?.subscribed ?? subscribed, unsubscribedAt: response.data?.unsubscribed_at ?? null,
             available: response.data?.available ?? true };
  },

  /**
   * Resolve the caller's effective role at the given scope. Returns null when
   * no scope id is supplied or the API has nothing to serve.
   */
  async effectiveRole(scope: EffectiveRoleScope): Promise<EffectiveRole | null> {
    const path = buildEffectiveRolePath(scope);
    if (!path) return null;
    const response = await apiClient.get<ApiEffectiveRoleResponse>(path, undefined);
    if (!response.data) return null;
    return {
      scope: response.data.scope,
      scopeId: response.data.scope_id,
      role: response.data.role,
      isSystemAdmin: response.data.is_system_admin,
    };
  },
};
